import { call, fork, put, takeEvery } from 'redux-saga/effects';
import { replace, push } from 'react-router-redux';
import { message } from 'antd';
import isEmpty from 'lodash/isEmpty';

import * as ga from '../redux/actions/app';
import {
  FETCH_USERS_MODULES_REQUEST,
  fetchUsersModulesFail,
  fetchUsersModulesRequest,
  fetchUsersModulesSuccess,
  POST_LOGIN_REQUEST,
  POST_LOGOUT_REQUEST,
  postLoginFail,
  postLoginSuccess,
  postLogoutDone,
  PRODUCT_LOGIN_REQUEST,
  productLoginFail,
  productLoginSuccess,
  PUT_CHANGE_PASSWORD_REQUEST,
  putChangePasswordFail,
  putChangePasswordSuccess,
  PUT_CHANGE_DEFAULT_ROLE_REQUEST,
  putChangeDefaultRoleFail,
  putChangeDefaultRoleSuccess,
  resetLogin,
  setCurrentProductLogin
} from '../redux/actions/authentication';
import {
  fetchResources,
  fetchUsersModules,
  postLogin,
  postLoginKeycloak,
  postLogout,
  putChangeDefaultRole,
  putChangePassword
} from '../services/authentication';
import { clearAuthenticationStorage } from '../util/localStorageUtils';
import { sortProductsOptions, filterRole } from '../services/util';
import UserService from '../services/keycloak';

/**
 * Handle authentication failure in saga by clearing storage and dispatching error
 * @param {string} errorMessage - The error message to display
 */
function* handleSagaAuthFailure(errorMessage) {
  yield put(ga.fetchFailed());
  yield put(postLoginFail({ message: errorMessage }));
  // Clear storage after dispatching actions
  clearAuthenticationStorage();
}

function* fetchUsersModulesWorker() {
  try {
    const usersModulesResponse = yield call(fetchUsersModules);

    if (usersModulesResponse.status === 200 && usersModulesResponse.data) {
      const modules = sortProductsOptions(usersModulesResponse.data || []);
      const usersModules = filterRole(modules);
      yield put(fetchUsersModulesSuccess(usersModules));

      return usersModules;
    } else {
      throw usersModulesResponse;
    }
  } catch (error) {
    yield put(fetchUsersModulesFail(error));
    return [];
  }
}

function* postLoginWorker(payload) {
  if (!isEmpty(payload)) {
    try {
      yield put(ga.fetchRequest({ text: 'login...' }));
      const authResponse = yield call(postLogin, payload);
      yield put(ga.fetchSuccess());

      //save token to localstorage
      if (authResponse.status === 200 && authResponse.data?.token) {
        // retrieve resource list
        localStorage.setItem('id_token', authResponse.data.token);
        localStorage.setItem('notification-status', true);
        const usersModules = yield fetchUsersModulesWorker();

        // Check if user has any valid roles
        if (!usersModules || usersModules.length === 0) {
          yield call(
            handleSagaAuthFailure,
            'Access Denied: No valid roles assigned to your account. Please contact your administrator.'
          );
          return;
        }

        const selectedProduct = usersModules[0];
        yield put(setCurrentProductLogin(selectedProduct));

        const CM_APPLICATION_ID = 7;
        const resourceResponse = yield call(
          fetchResources,
          selectedProduct ? selectedProduct.applicationId : CM_APPLICATION_ID,
          selectedProduct ? selectedProduct.roleCode : null
        );
        if (resourceResponse.status === 200) {
          const resourceDataList = resourceResponse.data.policies;
          yield put(postLoginSuccess(resourceDataList));
        }
      }
    } catch (error) {
      yield call(handleSagaAuthFailure, error.message || error);
    }
  } else {
    try {
      yield put(ga.fetchRequest({ text: 'login...' }));
      yield call(postLoginKeycloak, payload);
      yield put(ga.fetchSuccess());

    } catch (error) {
      yield call(handleSagaAuthFailure, error.message || error);
    }
  }
}

function* productLoginWorker(payload) {
  try {
    const token = localStorage.getItem('id_token');
    const { applicationId, roleCode } = payload;

    const resourceResponse = yield call(
      fetchResources,
      applicationId,
      roleCode
    );

    if (resourceResponse.status === 200) {
      yield put(setCurrentProductLogin(payload));

      const resourceDataList = resourceResponse.data.policies;
      yield put(productLoginSuccess(resourceDataList));
      yield put(push('/app'));
    } else {
      throw resourceResponse;
    }
  } catch (error) {
    yield put(ga.fetchFailed());
    yield put(productLoginFail(error));
  }
}

function* postChangePasswordWorker(payload) {
  try {
    yield put(ga.fetchRequest({ text: 'change password...' }));
    const result = yield call(putChangePassword, payload);
    yield put(ga.fetchSuccess());

    yield put(putChangePasswordSuccess(result));
  } catch (error) {
    yield put(ga.fetchFailed());
    yield put(putChangePasswordFail(error));
  }
}

function* putChangeDefaultRoleWorker(payload) {
  try {
    const token = localStorage.getItem('id_token');
    const { roleCode, moduleCode, isDefault } = payload;

    const result = yield call(
      putChangeDefaultRole,
      token,
      roleCode,
      moduleCode,
      isDefault
    );

    if (result.status === 200 && result.data) {
      yield put(fetchUsersModulesRequest());
      yield put(putChangeDefaultRoleSuccess(result));
      if (result?.data?.message) {
        const successMessage = result.data.message.replace(/.*:(.*)\)/, '$1');
        message.success(successMessage);
      }
    } else {
      throw result;
    }
  } catch (error) {
    yield put(putChangeDefaultRoleFail(error));
    if (error.data?.message) {
      message.success(error.data?.message);
    }
  }
}

function* postLogoutWorker(payload) {
  try {
    yield put(ga.fetchRequest({ text: 'logout...' }));
    yield call(postLogout, payload);
    yield put(ga.fetchSuccess());
    clearAuthenticationStorage();
  } catch (error) {
    yield put(ga.fetchFailed());
  }
  if (UserService.isAuthenticated()) {
    put(postLogoutDone());
    put(replace('/login'));
    UserService.doLogout().then(function () {
      location.reload();
    });
  } else {
    yield put(postLogoutDone());
    yield put(replace('/login'));
    location.reload();
  }
}

function* resetLoginWorker(payload) {
  yield put(resetLogin());
}

export function* watchLogin() {
  yield takeEvery(POST_LOGIN_REQUEST, function* ({ payload }) {
    yield fork(postLoginWorker, payload);
  });
  yield takeEvery(PRODUCT_LOGIN_REQUEST, function* ({ payload }) {
    yield fork(productLoginWorker, payload);
  });
}

export function* watchUserModules() {
  yield takeEvery(FETCH_USERS_MODULES_REQUEST, function* ({ payload }) {
    yield fork(fetchUsersModulesWorker, payload);
  });
}

export function* watchChangePassword() {
  yield takeEvery(PUT_CHANGE_PASSWORD_REQUEST, function* ({ payload }) {
    yield fork(postChangePasswordWorker, payload);
  });
}

export function* watchChangeRole() {
  yield takeEvery(PUT_CHANGE_DEFAULT_ROLE_REQUEST, function* ({ payload }) {
    yield fork(putChangeDefaultRoleWorker, payload);
  });
}

export function* watchLogout() {
  yield takeEvery(POST_LOGOUT_REQUEST, function* ({ payload }) {
    yield fork(postLogoutWorker, { payload });
  });
}

export function* watchResetLogout() {
  yield fork(resetLoginWorker);
}
